import { getAttachmentUrlForClassic } from 'owa-attachment-url';
import { MailboxInfo } from 'owa-client-ids';
import { getExtensionFromFileName, isImageFile } from 'owa-file';
import AttachmentUrlType from 'owa-files-url/lib/schema/AttachmentUrlType';
import { format } from 'owa-localize';
import AttachmentId from 'owa-service/lib/contract/AttachmentId';
import Item from 'owa-service/lib/contract/Item';
import * as trace from 'owa-trace';

const CID_PREFIX = 'cid:';
const EXTRACT_URL_REGEX = /url\("(.*)"\)/g;
const BACKGROUND_IMAGE_URL_FORMAT = 'url("{0}")';
const IMG_ELEMENT_WITH_CID_SELECTOR = 'img[src^="cid:"]';
const DIV_ELEMENT_WITH_BACKGROUND_CID_SELECTOR = "div[style*='background-image: url(\"cid:']";

export default function fixAttachedInlineImagesForMessageCard(
    cardElement: HTMLElement,
    item: Item,
    mailboxInfo: MailboxInfo
) {
    // Query selector finds all img elements with a cid: prefix
    const attachedImgElements = cardElement.querySelectorAll(IMG_ELEMENT_WITH_CID_SELECTOR);
    // Go through each matching element and fix up the img src
    if (attachedImgElements) {
        for (let i = 0; i < attachedImgElements.length; i++) {
            fixSrcForAttachedImage(<HTMLImageElement>attachedImgElements[i], item, mailboxInfo);
        }
    }

    // Query selector finds all div elements with an inline style property that looks like background-image: url("cid:*
    const elementsWithAttachedBackgroundImage = cardElement.querySelectorAll(
        DIV_ELEMENT_WITH_BACKGROUND_CID_SELECTOR
    );
    // Go through each matching element and fix up the img src
    if (elementsWithAttachedBackgroundImage) {
        for (let i = 0; i < elementsWithAttachedBackgroundImage.length; i++) {
            fixSrcForElementWithAttachedBackgroundImage(
                <HTMLElement>elementsWithAttachedBackgroundImage[i],
                item,
                mailboxInfo
            );
        }
    }
}

// Check the image src to see if it points to a cid, and if it does find the attachment for the
// img based on the cid and get the download url for it
function fixSrcForAttachedImage(img: HTMLImageElement, item: Item, mailboxInfo: MailboxInfo) {
    if (img.src && img.src.indexOf(CID_PREFIX) === 0) {
        const imgDownloadUrl = getAttachmentUrlForCid(img.src, item, mailboxInfo);
        if (imgDownloadUrl) {
            img.src = imgDownloadUrl;
        }
    }
}

// Extract the backgroundImage style and replace the cid: url with the correct attachment url
function fixSrcForElementWithAttachedBackgroundImage(
    elementWithBackgroundImage: HTMLElement,
    item: Item,
    mailboxInfo: MailboxInfo
) {
    let backgroundImage = elementWithBackgroundImage.style.backgroundImage;
    var match = EXTRACT_URL_REGEX.exec(backgroundImage);
    if (match && match.length > 1 && match[1]) {
        const imgDownloadUrl = getAttachmentUrlForCid(match[1], item, mailboxInfo);
        if (imgDownloadUrl) {
            elementWithBackgroundImage.style.backgroundImage = format(
                BACKGROUND_IMAGE_URL_FORMAT,
                imgDownloadUrl
            );
        }
    }
}

function getAttachmentUrlForCid(cidValue: string, item: Item, mailboxInfo: MailboxInfo): string {
    if (!item.Attachments) {
        return null;
    }

    const cid = stripImageExtension(cidValue.substring(CID_PREFIX.length));
    let attachmentId: AttachmentId;

    for (let i = 0; i < item.Attachments.length; i++) {
        const attachment = item.Attachments[i];
        if (
            cid === stripImageExtension(attachment.Name) ||
            cid === stripImageExtension(attachment.ContentId)
        ) {
            attachmentId = attachment.AttachmentId;
            break;
        }
    }

    if (!attachmentId) {
        trace.errorThatWillCauseAlert('fixSrcForImage: attachment id not found for cid: ' + cid);
        return null;
    }

    const imgDownloadUrl = getAttachmentUrlForClassic(
        {
            ...attachmentId,
            mailboxInfo: mailboxInfo,
        },
        AttachmentUrlType.Preview
    );

    if (!imgDownloadUrl) {
        trace.errorThatWillCauseAlert(
            'fixSrcForImage: could not create attachment url for image with cid: ' +
                cid +
                ' and attachment id: ' +
                attachmentId
        );
        return null;
    }

    return imgDownloadUrl;
}

function stripImageExtension(filename: string): string {
    if (isImageFile(filename)) {
        const imgExtension = getExtensionFromFileName(filename);
        return filename.substring(0, filename.length - imgExtension.length);
    }

    return filename;
}
